import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, Subject, throwError } from 'rxjs';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { CdmDeviceUiApiService, DeviceDiscoveryEvent } from '../api';
import { CdmDeviceUiDevice } from '../api/models/cdm-device-ui-device';

import {
	CdmDeviceUiDeviceQueryGQL,
	CdmDeviceUiDiscoveredDevicesSubscriptionGQL
} from './graphql/generated/cdm-device-ui.generated';
import { mapCdmDeviceUiDeviceFromSysApi } from './mappers/map-cdm-device-ui-device';

@Injectable()
export class SysApiCdmDeviceUiApiService implements CdmDeviceUiApiService {
	private readonly logger = inject(ILogger).createScopedLogger('SysApiCdmDeviceUiApiService');
	private readonly deviceQuery = inject(CdmDeviceUiDeviceQueryGQL);
	private readonly discoveredDevicesSubscription = inject(CdmDeviceUiDiscoveredDevicesSubscriptionGQL);
	private readonly destroyRef = inject(DestroyRef);

	private discoveryEvents?: Subject<DeviceDiscoveryEvent>;

	public getDevice$(deviceId: string): Observable<CdmDeviceUiDevice> {
		return this.deviceQuery.watch({ variables: { deviceId }, fetchPolicy: 'cache-first' }).valueChanges.pipe(
			takeUntilDestroyed(this.destroyRef),
			waitForGQLCompleteQueryOrThrow(),
			map((result) => {
				if (result.data?.node && '__typename' in result.data.node) {
					return mapCdmDeviceUiDeviceFromSysApi(result.data.node);
				}

				throw ApolloQueryErrorMapper.getError(result);
			}),
			catchError((error) => {
				this.logger.error('getDevice$()', 'Error fetching device', { deviceId, error });
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public subscribeDiscoveredDevices$(): Observable<DeviceDiscoveryEvent> {
		if (this.discoveryEvents) {
			return this.discoveryEvents.asObservable();
		}

		this.discoveryEvents = new Subject<DeviceDiscoveryEvent>();
		this.discoveredDevicesSubscription
			.subscribe({
				errorPolicy: 'ignore',
				fetchPolicy: 'no-cache'
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result.data) {
					if ('added' in result.data.discoveredDevices) {
						this.discoveryEvents?.next({
							type: 'deviceAdded',
							id: result.data.discoveredDevices.added.id
						});
					} else if ('removed' in result.data.discoveredDevices) {
						this.discoveryEvents?.next({
							type: 'deviceRemoved',
							id: result.data.discoveredDevices.removed
						});
					}
				}
			});

		return this.discoveryEvents.asObservable();
	}
}
